import { useEffect, useMemo, useRef, useState } from 'react'
import { useGetPublicTopics } from '@/hooks/api/dashboard/useGetPublicTopics'
import { Topic } from '@/types'
import { TopicType } from '@/utils/constants'
import { createAnonymousApi } from '@/utils/anonymousApi'
import { AlbumSummary, GallerySummary } from '@/features/album/types'
import { topicToGallerySummary } from '@/features/album/mappers'
import { topicToDiscoverAlbum, topicToDiscoverGallery } from '../mappers'

const anonymousApi = createAnonymousApi()

export interface DiscoverFeedItem {
  id: string
  kind: 'album' | 'gallery'
  album?: AlbumSummary
  gallery?: GallerySummary
  likes: number
}

export interface UseDiscoverFeedResult {
  albums: AlbumSummary[]
  galleries: GallerySummary[]
  /** Both types merged, newest-first by topicID — backs the "All" tab's mixed grid. */
  items: DiscoverFeedItem[]
  /** True until both the album and gallery first pages have resolved. */
  loading: boolean
  loadingMore: boolean
  error: unknown
  hasMore: boolean
  loadMore: () => void
}

// The public-topics API has no total-count/hasMore flag (see useGetPublicTopics.ts) —
// a short page is the only signal available that there's nothing left to fetch.
const PAGE_SIZE_HINT = 20

function byTopicIdDesc(a: Topic, b: Topic): number {
  return Number(b.topicID) - Number(a.topicID)
}

/**
 * Public Discover feed — two `useGetPublicTopics({ interaction: 1 })` calls,
 * one per `topicType`, merged client-side.
 *
 * This used to be a single `topicType: ALL` call, but the backend's
 * `topicApplication` param (see `TopicTypeAndApplicationDic`) partitions
 * `/ajax/topics` by app namespace, and `ALL` resolves to the
 * gallery/pattern namespace (20) — never the album namespace (2). A single
 * `ALL` request can *only* ever return galleries/patterns; every public
 * album was silently excluded regardless of how many existed. Fetching
 * `ALBUM` and `GALLERY` explicitly, each with its own correct
 * `topicApplication`, is the only way to get both.
 *
 * Deliberately built on `useGetPublicTopics` (a plain, unauthenticated
 * `useQuery`) rather than the `useGetTopics` mutation `/albums`/`/galleries`
 * use — that mutation writes into the shared `topicList` Zustand slice,
 * which would mix other users' public topics into "my albums" the next
 * time that slice is read. Discover's results stay local to this hook.
 */
export function useDiscoverFeed(): UseDiscoverFeedResult {
  const [albumPageNo, setAlbumPageNo] = useState(1)
  const [galleryPageNo, setGalleryPageNo] = useState(1)
  const [albumsById, setAlbumsById] = useState<Record<string, Topic>>({})
  const [galleriesById, setGalleriesById] = useState<Record<string, Topic>>({})
  const [albumHasMore, setAlbumHasMore] = useState(true)
  const [galleryHasMore, setGalleryHasMore] = useState(true)

  const albumQuery = useGetPublicTopics({
    pageNo: albumPageNo,
    interaction: 1,
    topicType: TopicType.ALBUM,
  })
  const galleryQuery = useGetPublicTopics({
    pageNo: galleryPageNo,
    interaction: 1,
    topicType: TopicType.GALLERY,
  })

  useEffect(() => {
    if (!albumQuery.data) return
    setAlbumsById((prev) => {
      const next = albumPageNo === 1 ? {} : { ...prev }
      albumQuery.data!.forEach((t) => {
        next[t.topicID] = t
      })
      return next
    })
    setAlbumHasMore(albumQuery.data.length >= PAGE_SIZE_HINT)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [albumQuery.data, albumPageNo])

  useEffect(() => {
    if (!galleryQuery.data) return
    setGalleriesById((prev) => {
      const next = galleryPageNo === 1 ? {} : { ...prev }
      galleryQuery.data!.forEach((t) => {
        next[t.topicID] = t
      })
      return next
    })
    setGalleryHasMore(galleryQuery.data.length >= PAGE_SIZE_HINT)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [galleryQuery.data, galleryPageNo])

  // `Object.values()` on a Record keyed by numeric-string topicIDs comes
  // back in *ascending integer* order per the JS spec, no matter what order
  // keys were inserted in — so an explicit sort is required to get a real
  // newest-first feed rather than silently oldest-first.
  const albums = useMemo(
    () =>
      Object.values(albumsById)
        .filter((t) => String(t.topicType) === String(TopicType.ALBUM))
        .sort(byTopicIdDesc)
        .map((t) => topicToDiscoverAlbum(t)),
    [albumsById],
  )

  const galleries = useMemo(
    () =>
      Object.values(galleriesById)
        .filter((t) => String(t.topicType) === String(TopicType.GALLERY))
        .sort(byTopicIdDesc)
        .map((t) => {
          // Free real-cover fallback when the source album happens to also
          // be public and already on this page's own albums fetch — same
          // idea as GalleryCard's `sourceAlbumCoverUrl`, no extra request.
          const sourceAlbumId = topicToGallerySummary(t).sourceAlbumId
          const sourceAlbumTopic = sourceAlbumId ? albumsById[sourceAlbumId] : undefined
          return topicToDiscoverGallery(t, sourceAlbumTopic)
        }),
    [galleriesById, albumsById],
  )

  // Mini list responses don't reliably carry a usable cover/print for every
  // topic — legacy albums can have `latestEgo: []` despite a real
  // `photoCount`, and a gallery's real `studio3dSceneStateV1` note is only
  // visible via `latestNote` when it happens to be the most recently
  // touched note (see mappers.ts). Rather than show a broken/empty card in
  // either case, bounded-enrich just those items with one `topicNoteList`
  // (full notes, unauthenticated — same endpoint `usePublicAlbum` uses) —
  // not every item, just the ones the cheap list call left with nothing.
  const enrichAttempted = useRef<Set<string>>(new Set())
  useEffect(() => {
    const albumTargets = albums.filter((a) => !a.coverUrl && !enrichAttempted.current.has(a.id))
    const galleryTargets = galleries.filter((g) => g.arts.length === 0 && !enrichAttempted.current.has(g.id))
    if (albumTargets.length === 0 && galleryTargets.length === 0) return
    ;[...albumTargets, ...galleryTargets].forEach((item) => enrichAttempted.current.add(item.id))

    albumTargets.forEach(async (a) => {
      try {
        const res = await anonymousApi.post('/ajax/topics', { requestType: 'topicNoteList', topicID: a.id, format: 'json', pageNo: 1 })
        if (res.data?.isError) return
        const notes = res.data?.notes || []
        if (notes.length === 0) return
        setAlbumsById((prev) => (prev[a.id] ? { ...prev, [a.id]: { ...prev[a.id], notes } } : prev))
      } catch {
        /* best effort — card just keeps its current (possibly empty) cover */
      }
    })
    galleryTargets.forEach(async (g) => {
      try {
        const res = await anonymousApi.post('/ajax/topics', { requestType: 'topicNoteList', topicID: g.id, format: 'json', pageNo: 1 })
        if (res.data?.isError) return
        const notes = res.data?.notes || []
        if (notes.length === 0) return
        setGalleriesById((prev) => (prev[g.id] ? { ...prev, [g.id]: { ...prev[g.id], notes } } : prev))
      } catch {
        /* best effort — card just keeps its current (possibly empty) arts */
      }
    })
  }, [albums, galleries])

  const items = useMemo<DiscoverFeedItem[]>(() => {
    const albumItems: DiscoverFeedItem[] = albums.map((a) => ({ id: a.id, kind: 'album', album: a, likes: a.likes || 0 }))
    const galleryItems: DiscoverFeedItem[] = galleries.map((g) => ({ id: g.id, kind: 'gallery', gallery: g, likes: g.likes || 0 }))
    return [...albumItems, ...galleryItems].sort((a, b) => Number(b.id) - Number(a.id))
  }, [albums, galleries])

  return {
    albums,
    galleries,
    items,
    loading: (albumPageNo === 1 && albumQuery.isLoading) || (galleryPageNo === 1 && galleryQuery.isLoading),
    loadingMore: (albumPageNo > 1 && albumQuery.isFetching) || (galleryPageNo > 1 && galleryQuery.isFetching),
    error: albumQuery.error || galleryQuery.error,
    hasMore: albumHasMore || galleryHasMore,
    loadMore: () => {
      if (albumHasMore && !albumQuery.isFetching) setAlbumPageNo((p) => p + 1)
      if (galleryHasMore && !galleryQuery.isFetching) setGalleryPageNo((p) => p + 1)
    },
  }
}
